function AdminBookingDetailsModal({ booking, onClose }) {
  if (!booking) return null;

  const {
    productName,
    destination,
    date,
    durationNights,
    durationDays,
    adults,
    children,
    currency,
    totalPrice,
    finalTotalPrice,
    discountType,
    discountValue,
    status,
    selectedRoom = {},
    selectedFlights = {},
    quoteLines = [],
    userId,
    user,
  } = booking;

  const client = userId || user || {};

  const segments = selectedFlights.segments || [];
  const luggagesSelection =
    selectedFlights.luggagesSelection || selectedFlights.luggages || null;

  const groupedSegments = React.useMemo(() => {
    const res = { outbound: [], inbound: [] };
    segments.forEach((s) => {
      if (s.direction === 'inbound') res.inbound.push(s);
      else res.outbound.push(s);
    });
    return res;
  }, [segments]);

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : '';

  const formatDateTime = (d) =>
    d
      ? new Date(d).toLocaleString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '';

  const fmtMoney = (v) =>
    `${Number(v || 0).toFixed(2)} ${currency || 'EUR'}`;

  return (
    <div className="modal-backdrop show" style={{ display: 'block' }}>
      <div className="modal d-block" tabIndex="-1">
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Détail réservation – {productName}
              </h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            <div className="modal-body">
              {/* Client */}
              <div className="mb-3">
                <h6 className="fw-semibold mb-1">Client</h6>
                <div>{client.companyName || '—'}</div>
                <div className="small text-muted">{client.email || ''}</div>
              </div>

              {/* Résumé séjour */}
              <div className="mb-3">
                <h6 className="fw-semibold mb-1">Séjour</h6>
                <div>{productName}</div>
                <div className="small text-muted">
                  {destination?.city} {destination?.country && `– ${destination.country}`}
                </div>
                <div className="small text-muted mt-1">
                  Départ le {formatDate(date)} · {durationNights || durationDays} nuits ·{' '}
                  {adults} ad, {children} enf
                </div>
                <div className="mt-2">
                  Sous-total : {fmtMoney(totalPrice)}{' '}
                  {discountValue ? (
                    <>
                      · Remise :{' '}
                      {discountType === 'amount'
                        ? fmtMoney(discountValue)
                        : `${discountValue} %`}
                      {' · '}
                      Total : <strong>{fmtMoney(finalTotalPrice)}</strong>
                    </>
                  ) : (
                    <>
                      · Total : <strong>{fmtMoney(finalTotalPrice)}</strong>
                    </>
                  )}
                  {' · '}
                  Statut : <span className="badge bg-secondary">{status}</span>
                </div>
              </div>

              {/* Hébergement */}
              <hr />
              <h6>Hébergement</h6>
              {selectedRoom && Object.keys(selectedRoom).length ? (
                <>
                  <div>{selectedRoom.description || selectedRoom.code}</div>
                  <ul className="small mt-1">
                    {selectedRoom.items?.map((it, i) => (
                      <li key={i}>
                        {it.label} – {it.roomType} · {it.nights} nuit(s) ·{' '}
                        {fmtMoney(it.total)}
                      </li>
                    ))}
                  </ul>
                  {selectedRoom.boardLabel && (
                    <div className="small mt-1">
                      Pension : {selectedRoom.boardLabel} (
                      {fmtMoney(selectedRoom.boardTotal)})
                    </div>
                  )}
                </>
              ) : (
                <div className="text-muted small">
                  Aucune chambre enregistrée.
                </div>
              )}

              {/* Vols */}
              <hr />
              <h6>Vols</h6>
              {segments.length === 0 ? (
                <div className="text-muted small">
                  Aucun détail de vol enregistré pour cette réservation.
                </div>
              ) : (
                <>
                  {groupedSegments.outbound.length > 0 && (
                    <div className="mb-2">
                      <div className="fw-semibold mb-1">Aller</div>
                      <ul className="small mb-0">
                        {groupedSegments.outbound.map((s, i) => (
                          <li key={`out-${i}`}>
                            {s.from} → {s.to} · {s.carrier} {s.flightNumber} ·
                            départ {formatDateTime(s.departure)} · arrivée{' '}
                            {formatDateTime(s.arrival)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {groupedSegments.inbound.length > 0 && (
                    <div>
                      <div className="fw-semibold mb-1">Retour</div>
                      <ul className="small mb-0">
                        {groupedSegments.inbound.map((s, i) => (
                          <li key={`in-${i}`}>
                            {s.from} → {s.to} · {s.carrier} {s.flightNumber} ·
                            départ {formatDateTime(s.departure)} · arrivée{' '}
                            {formatDateTime(s.arrival)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}

              {/* Bagages */}
              <hr />
              <h6>Bagages</h6>
              {luggagesSelection ? (
                <pre className="small bg-light p-2 rounded">
                  {JSON.stringify(luggagesSelection, null, 2)}
                </pre>
              ) : (
                <div className="text-muted small">
                  Aucun supplément bagage sélectionné.
                </div>
              )}

              {/* Devis détaillé */}
              <hr />
              <h6>Détail du devis</h6>
              {quoteLines && quoteLines.length ? (
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Libellé</th>
                      <th className="text-end">Qté</th>
                      <th className="text-end">PU</th>
                      <th className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quoteLines.map((l, i) => (
                      <tr key={i}>
                        <td className="small text-muted">{l.kind}</td>
                        <td>{l.label}</td>
                        <td className="text-end">{l.qty}</td>
                        <td className="text-end">{fmtMoney(l.unit)}</td>
                        <td className="text-end">{fmtMoney(l.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-muted small">
                  Pas de détail de devis enregistré.
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminBookingDetailsModal
